import React from 'react';
import { useSelector } from 'react-redux';

import { useFormikContext } from 'formik';
import { QRCodeCanvas } from 'qrcode.react';

import { AppConfig } from '@helpers/appConfig';
import { isDesktop } from '@helpers/isDesktop';
import { Utils } from '@helpers/utils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Paper, Typography } from '@mui/material';
import { RootState } from '@store/rootReducer';

import { useT } from '../../../hooks/useT';
import { OOControlModel } from '../../../models/ControlModel';

import styles from './QRCodeDisplay.module.scss';

interface QRCodeDisplayProps {
  controlModel: OOControlModel;
}

export const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({ controlModel }) => {
  const { t } = useT('entry');

  const utils = new Utils();
  const appConfig = new AppConfig(utils);

  const { tenantId } = useSelector((state: RootState) => state.tenant);
  const { userId } = useSelector((state: RootState) => state.authApp)!;
  const { clientOrganizationId, selectedConfigurationId, applicationId } = useSelector(
    (state: RootState) => state.oneOnboarding,
  );

  const { values } = useFormikContext<Record<string, any>>();
  const consentChecked = Boolean(values?.DEVICE_CHECK_CONSENT);

  const { staticText, description, label } = controlModel;

  const link = `${appConfig.frontendUrl}/app/login?tenantId=${tenantId}&candidateId=${userId}&clientOrganizationId=${clientOrganizationId}&applicationId=${applicationId}&configurationId=${selectedConfigurationId}`;

  // Show recruiter message
  if (consentChecked) {
    return (
      <Paper className={styles.recruiterBox} elevation={2}>
        <InfoOutlinedIcon sx={{ mt: 0.3 }} />
        <Typography className={styles.recruiterBox__text}>{t(`entry:${staticText}`)}</Typography>
      </Paper>
    );
  }

  // hiding QR on mobile ( as per requirement )
  if (!isDesktop()) return null;

  return (
    <Paper className={styles.qrCard} elevation={2}>
      {/* LEFT SIDE */}
      <div className={styles.qrCard__left}>
        <div className={styles.qrCard__header}>
          <InfoOutlinedIcon />

          <Typography className={styles.qrCard__title}>{t(`entry:${label}`)}</Typography>
        </div>

        <Typography className={styles.qrCard__description}>{t(`entry:${description}`)}</Typography>
      </div>

      {/* QR Code */}
      <div className={styles.qrCard__right}>
        <QRCodeCanvas value={link} size={108} />
      </div>
    </Paper>
  );
};
